import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CustomDefinition } from "../../../interfaces/db";

export enum CustomScoresView {
  LIST = 'list',
  EDITING = 'editing', 
  TESTING = 'testing'
}

export interface CustomScoresState {
  currentView: CustomScoresView;
  editingDefinition: CustomDefinition | null;
  testingMetric: CustomDefinition | null;
  templateTab: string;
  hasUnsavedChanges: boolean;
  error: string | null;
  snackbarOpen: boolean;
}

const customScoresInitialState: CustomScoresState = {
  currentView: CustomScoresView.LIST,
  editingDefinition: null,
  testingMetric: null,
  templateTab: 'sales',
  hasUnsavedChanges: false,
  error: null,
  snackbarOpen: false,
};

export const customScoresSlice = createSlice({
  name: 'customScores',
  initialState: customScoresInitialState,
  reducers: {
    // View management
    setView: (state, action: PayloadAction<CustomScoresView>) => {
      state.currentView = action.payload;
      // Clear modals when going back to list view
      if (action.payload === CustomScoresView.LIST) {
        state.editingDefinition = null;
        state.testingMetric = null;
      }
    },

    // Edit modal management
    openEditModal: (state, action: PayloadAction<CustomDefinition | null>) => {
      state.currentView = CustomScoresView.EDITING;
      state.editingDefinition = action.payload;
      state.testingMetric = null;
    },
    closeEditModal: (state) => {
      state.currentView = CustomScoresView.LIST;
      state.editingDefinition = null;
    },

    // Test modal management  
    openTestModal: (state, action: PayloadAction<CustomDefinition>) => {
      state.currentView = CustomScoresView.TESTING;
      state.testingMetric = action.payload;
      state.editingDefinition = null;
    },
    closeTestModal: (state) => {
      state.currentView = CustomScoresView.LIST;
      state.testingMetric = null;
    },

    // Navigation between modals
    switchFromEditToTest: (state, action: PayloadAction<CustomDefinition>) => {
      state.currentView = CustomScoresView.TESTING;
      state.editingDefinition = null;
      state.testingMetric = action.payload;
    },
    switchFromTestToEdit: (state, action: PayloadAction<CustomDefinition>) => {
      state.currentView = CustomScoresView.EDITING;
      state.testingMetric = null;
      state.editingDefinition = action.payload;
    },

    // Template tab
    setTemplateTab: (state, action: PayloadAction<string>) => {
      state.templateTab = action.payload;
    },

    // Change tracking
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },

    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.snackbarOpen = action.payload !== null;
    },
    clearError: (state) => {
      state.error = null;
      state.snackbarOpen = false;
    },
    setSnackbarOpen: (state, action: PayloadAction<boolean>) => {
      state.snackbarOpen = action.payload;
      if (!action.payload) {
        state.error = null;
      }
    },
  }
});

export const {
  setView,
  openEditModal,
  closeEditModal,
  openTestModal,
  closeTestModal,
  switchFromEditToTest,
  switchFromTestToEdit,
  setTemplateTab,
  setHasUnsavedChanges,
  setError,
  clearError,
  setSnackbarOpen,
} = customScoresSlice.actions;

export default customScoresSlice.reducer; 